import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import './Dashboard.css';
import { useNavigate } from 'react-router-dom';
import { getKnownFacesDataFromDB } from '../services/data';

const Dashboard = () => {
  const [selectedTab, setSelectedTab] = useState('allUsers');
  const [selectedUser, setSelectedUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newUsers, setNewUsers] = useState({});
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUserData = async () => {
      const userDetailsCookie = Cookies.get('facereco_user_details');
      if (userDetailsCookie) {
        const parsedUserDetails = JSON.parse(userDetailsCookie);
        const data = {
          'email': parsedUserDetails.email,
        }
        const response = await getKnownFacesDataFromDB(data);
        setAllUsers(Object.values(response.faces));
      } else {
        navigate('/');
      }
    };
    loadUserData();
  }, [navigate]);

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
    setSelectedUser(null); // Reset selected user when changing tabs
    setSidebarVisible(false);
  };

  const handleUserClick = (user) => {
    setSelectedTab('userDetails');
    setSelectedUser(user);
  };

  const handleAddFromFolder = () => {
    setSelectedOption('folder');
    fileInputRef.current?.click();
  };

  const handleAddFromExcel = () => {
    // Logic to handle "Add from Excel File" option
    setSelectedOption('excel');
  };

  const handleFileInputChange = async (e) => {
    const files = e.target.files;
    setSelectedFiles([...selectedFiles, ...files]);

    const filePromises = Array.from(files).map((file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({ file, dataURL: reader.result });
        };
        reader.onerror = () => {
          reject(new Error('Error reading the file'));
        };
        reader.readAsDataURL(file);
      });
    });

    try {
      const fileResults = await Promise.all(filePromises);

      const updatedUsers = { ...newUsers };

      fileResults.forEach(({ file, dataURL }) => {
        const detailsArray = file.name.split('_');
        var email = '';
        if (detailsArray[0].includes('-'))
          email = detailsArray[0].replace(/-/g, '.') + '@' + detailsArray[1] + '.' + detailsArray[2];
        else
          email = detailsArray[0] + '@' + detailsArray[1] + '.' + detailsArray[2];
        var name = detailsArray[3] + ' ';
        if (detailsArray[4] === '-')
          name += detailsArray[5];
        else
          name += (detailsArray[4] + ' ' + detailsArray[5]);

        const age = detailsArray[6];
        const gender = detailsArray[7] === 'M' ? 'Male' : 'Female';

        if (updatedUsers[email]) {
          updatedUsers[email].images.push(dataURL);
        } else {
          updatedUsers[email] = {
            name,
            age,
            gender,
            images: [dataURL],
          };
        }
      });

      setNewUsers(updatedUsers);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveUser = (email) => {
    const updatedUsers = { ...newUsers };
    delete updatedUsers[email];
    setNewUsers(updatedUsers);
  };

  return (
    <div>
      <div className="dashboard-navbar">
        <button className="dashboard-hamburger" onClick={() => setSidebarVisible(!sidebarVisible)}>
          &#9776;
        </button>
      </div>
      <div className="dashboard-container">
        <div className={`dashboard-left-sidebar ${sidebarVisible ? 'show' : ''}`}>
          <h3>Dashboard</h3>
          <div className="dashboard-sidebar-button" onClick={() => handleTabClick('allUsers')}>
            All Users
          </div>
          <div className="dashboard-sidebar-button" onClick={() => handleTabClick('analytics')}>
            Analytics
          </div>
          <div className="dashboard-sidebar-button" onClick={() => handleTabClick('addUser')}>
            Add User
          </div>
          {/* Add more buttons for other tabs */}
        </div>

        <div className={`dashboard-right-content ${sidebarVisible ? 'hide' : ''}`}>
          {selectedTab === 'allUsers' && (
            <div className="dashboard-user-list">
              {allUsers.map((user) => (
                <div key={user.email} className="dashboard-user-card" onClick={() => handleUserClick(user)}>
                  <p>{user.name}</p>
                  <p>Email: {user.email}</p>
                  {/* Add more details if needed */}
                </div>
              ))}
            </div>
          )}

          {selectedTab === 'userDetails' && selectedUser && (
            <div className="dashboard-user-details">
              <h2>User Details</h2>
              <p>Name: {selectedUser.name}</p>
              <p>Age: {selectedUser.age}</p>
              <p>Email: {selectedUser.email}</p>
              {/* Add more details about the selected user */}
            </div>
          )}

          {/* Add other components for different tabs */}
          {selectedTab === 'addUser' && (
            <div className="dashboard-add-user">
              <h2>Add User</h2>
              <div className="add-user-options">
                <button onClick={handleAddFromFolder}>Add Folder</button>
                <button onClick={handleAddFromExcel}>Add Excel File</button>
              </div>
              <div className={`add-from-folder-section ${selectedOption === 'folder' ? 'show' : ''}`}>
                {/* Code to handle "Add from Folder" option */}
                <input
                  ref={fileInputRef}
                  type="file"
                  directory=""
                  webkitdirectory=""
                  multiple
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />

                <div className="preview-images-container">
                  {Object.entries(newUsers).map(([email, userData]) => (
                    <div key={email} className="preview-image-card">
                      {/* Display user details and images */}
                      <div className='preview-image-card-user-details'>
                        <h3>{userData.name}</h3>
                        <p>Email: {email}</p>
                        <p>Age: {userData.age}</p>
                        <p>Gender: {userData.gender}</p>
                        <button onClick={() => handleRemoveUser(email)}>Remove</button>
                      </div>
                      <div className="preview-images">
                        {userData.images.map((image, index) => (
                          <img key={index} src={image} alt={`Preview ${index}`} />
                        ))}
                      </div>

                    </div>
                  ))}
                </div>

              </div>
              <div className={`add-from-excel-section ${selectedOption === 'excel' ? 'show' : ''}`}>
                {/* Code to handle "Add from Excel File" option */}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
